import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  Sparkles,
  Bot,
  Shield,
  Eye,
  Lock,
  Loader2,
  CheckCircle2,
  Circle,
  AlertTriangle,
  TrendingUp,
} from "lucide-react";
import AgentUsageSparkline from "@/components/AgentUsageSparkline";
import OrchestrationsCard from "@/components/OrchestrationsCard";

/**
 * SettingsAgents — Workspace admin page pour gérer les agents IA.
 *
 * V23.15.10 — Architecture additive : les agents sont une couche d'automatisation
 * gérable par l'admin du workspace (pas seulement Ultra Admin).
 */

const CATEGORY_ICONS = {
  copilote: Bot,
  coach: Sparkles,
  sentinelle: Shield,
  anticipation: Eye,
};

const CATEGORY_COLOR = {
  copilote: "#378ADD",
  coach: "#EF9F27",
  sentinelle: "#10B981",
  anticipation: "#A855F7",
};

export default function SettingsAgents() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busySlug, setBusySlug] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/workspace/agents");
      setData(r.data);
    } catch (e) {
      setError(fmtError(e, "Chargement impossible"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async (slug, enable) => {
    setBusySlug(slug);
    try {
      await api.post("/workspace/agents/toggle", { agent: slug, enable });
      notify.success(enable ? "Agent activé" : "Agent désactivé");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Toggle échoué";
      notify.error(detail);
    } finally {
      setBusySlug(null);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-zinc-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement des agents…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-[#E24B4A] text-sm">{error}</div>
    );
  }

  const items = data?.items || [];
  const planName = data?.plan?.name || data?.plan?.id || "—";
  const activeCount = data?.active_count ?? 0;
  const includedCount = data?.included_count ?? 0;
  const period = data?.period || "—";

  return (
    <div className="space-y-6 p-6 max-w-5xl" data-testid="settings-agents-page">
      <div>
        <div className="noam-overline">Paramètres · Agents IA</div>
        <h1 className="noam-display text-2xl font-semibold mt-1">
          Agents IA de votre workspace
        </h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
          Les agents sont une couche d&apos;automatisation au-dessus de Noam. Votre environnement
          reste pleinement fonctionnel sans aucun agent actif — les agents ajoutent du
          confort (navigation, prefill, surveillance proactive, coaching métier).
        </p>
      </div>

      <div className="noam-card p-5 flex items-center gap-6 flex-wrap" data-testid="agents-plan-banner">
        <div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Plan en cours</div>
          <div className="text-base font-medium text-zinc-100 mt-0.5">{planName}</div>
        </div>
        <div className="h-8 w-px bg-white/10" />
        <div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Agents actifs</div>
          <div className="text-base font-medium text-zinc-100 mt-0.5">
            {activeCount} <span className="text-zinc-500">/ {includedCount} inclus</span>
          </div>
        </div>
        <div className="h-8 w-px bg-white/10" />
        <div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Période d&apos;usage</div>
          <div className="text-base font-medium text-zinc-100 mt-0.5">{period}</div>
        </div>
      </div>

      {/* V23.15.22 — Synchronisation de conscience du Copilote IA (Doctrine 49) */}
      <ContextSyncSection />

      {/* V23.15.23 — Orchestration inter-agents (Doctrine 49 P3) */}
      <OrchestrationsCard />

      <div className="grid gap-3">
        {items.map((a) => (
          <AgentCard
            key={a.slug}
            agent={a}
            busy={busySlug === a.slug}
            onToggle={toggle}
          />
        ))}
      </div>
    </div>
  );
}

function AgentCard({ agent: a, busy, onToggle }) {
  const Icon = CATEGORY_ICONS[a.category] || Bot;
  const color = CATEGORY_COLOR[a.category] || "#378ADD";

  const isComingSoon = a.status === "coming_soon";
  const isLocked = a.locked;
  const notInPlan = !a.in_plan;
  const canToggle = !isLocked && !isComingSoon && a.in_plan;

  let statusLabel = "—";
  let statusClass = "text-zinc-500";
  if (isLocked) {
    statusLabel = "Verrouillé par Ultra Admin";
    statusClass = "text-[#E24B4A]";
  } else if (notInPlan) {
    statusLabel = "Upgrade plan requis";
    statusClass = "text-[#EF9F27]";
  } else if (isComingSoon) {
    statusLabel = "En préparation";
    statusClass = "text-[#A855F7]";
  } else if (a.active) {
    statusLabel = "Actif";
    statusClass = "text-emerald-400";
  } else {
    statusLabel = "Inactif";
    statusClass = "text-zinc-500";
  }

  const usage = a.usage || {};
  const quotaPct = a.quota_pct;

  return (
    <div
      className={`noam-card p-5 transition-opacity ${notInPlan || isComingSoon ? "opacity-70" : ""}`}
      data-testid={`agent-card-${a.slug}`}
    >
      <div className="flex items-start gap-4">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: `${color}15`, border: `1px solid ${color}40` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-base font-medium text-zinc-100">{a.name}</div>
            <span
              className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
              style={{
                background: `${color}15`,
                color,
                border: `1px solid ${color}40`,
              }}
            >
              {a.category}
            </span>
            {isComingSoon && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#A855F7]/15 text-[#A855F7] border border-[#A855F7]/40">
                Bientôt
              </span>
            )}
            {isLocked && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E24B4A]/15 text-[#E24B4A] border border-[#E24B4A]/40 inline-flex items-center gap-1">
                <Lock className="w-2.5 h-2.5" /> Verrouillé
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-300 mt-2 leading-relaxed">{a.description}</p>

          <div className="mt-3 flex items-center gap-4 flex-wrap text-[11px]">
            <div className={`inline-flex items-center gap-1 ${statusClass}`} data-testid={`agent-status-${a.slug}`}>
              {a.active ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
              {statusLabel}
            </div>
            {(a.active || usage.calls > 0) && (
              <div className="inline-flex items-center gap-1 text-zinc-500">
                <TrendingUp className="w-3 h-3" />
                {usage.calls || 0} appel(s) ce mois
                {a.soft_quota && (
                  <span className="text-zinc-600">· quota {a.soft_quota}</span>
                )}
              </div>
            )}
            {/* V23.15.11 — Sparkline 30j inline */}
            {!isComingSoon && a.in_plan && (
              <AgentUsageSparkline slug={a.slug} color={color} days={30} />
            )}
            {a.soft_warning && (
              <div
                className="inline-flex items-center gap-1 text-[#EF9F27] font-medium"
                data-testid={`agent-soft-warning-${a.slug}`}
              >
                <AlertTriangle className="w-3 h-3" />
                Quota à {quotaPct}% — vous approchez de la limite mensuelle
              </div>
            )}
          </div>
        </div>

        <div className="shrink-0">
          {canToggle ? (
            <ToggleSwitch
              active={a.active}
              busy={busy}
              onToggle={() => onToggle(a.slug, !a.active)}
              testId={`agent-toggle-${a.slug}`}
            />
          ) : (
            <div className="text-[10px] text-zinc-600 text-right max-w-[140px]">
              {isLocked
                ? "Action désactivée par l'admin Noam"
                : isComingSoon
                  ? "En cours de développement"
                  : "Disponible avec un plan supérieur"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ToggleSwitch({ active, busy, onToggle, testId }) {
  return (
    <button
      onClick={onToggle}
      disabled={busy}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        active ? "bg-emerald-500" : "bg-zinc-700"
      } ${busy ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
      data-testid={testId}
      aria-pressed={active}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          active ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

// V23.15.22 — Section synchronisation de conscience (Doctrine 49).
// Affiche : statut + toggle (admin) + lien vers journal.
function ContextSyncSection() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/workspace/context-sync/status");
      setStatus(r.data);
    } catch {
      // Endpoint indisponible (toggle global OFF côté plateforme) — section silencieuse.
      setStatus({ unavailable: true });
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (next) => {
    setBusy(true);
    try {
      const r = await api.post("/workspace/context-sync/toggle", { enabled: next });
      notify.success(next ? "Synchronisation activée" : "Synchronisation désactivée");
      setStatus((s) => ({ ...(s || {}), enabled_org: r.data?.enabled_org, effective_enabled: r.data?.enabled_org && (s?.enabled_global !== false) }));
    } catch (e) {
      notify.error(fmtError(e, "Toggle failed"));
    } finally {
      setBusy(false);
    }
  };

  if (!status || status.unavailable) return null;

  const enabledGlobal = status.enabled_global !== false;
  const enabledOrg = status.enabled_org !== false;
  const effective = enabledGlobal && enabledOrg;
  const lastSyncedAt = status.last_synced_at;
  const todayCount = status.today_count || 0;

  return (
    <div className="noam-card p-5 space-y-3" data-testid="context-sync-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#378ADD]" />
            <div className="noam-overline">Doctrine 49 · Synchronisation de conscience</div>
          </div>
          <h2 className="text-base font-medium text-zinc-100 mt-1">
            Le Copilote IA reste aligné en temps réel avec votre workspace
          </h2>
          <p className="text-xs text-zinc-300 mt-1.5 max-w-2xl leading-relaxed">
            Chaque modification significative (risques, annuaire, opportunités, négociations, GRC…)
            est captée comme un micro-enregistrement et injectée dans la mémoire du Copilote.
            Plus de décalage entre ce que vous changez et ce qu&apos;il sait.
          </p>
        </div>
        <Toggle
          active={enabledOrg}
          disabled={busy || !enabledGlobal}
          onChange={() => toggle(!enabledOrg)}
          testId="context-sync-toggle"
        />
      </div>

      <div className="flex items-center gap-5 text-[11px] text-zinc-500 flex-wrap pt-2 border-t border-white/5">
        <span data-testid="context-sync-effective">
          État effectif :{" "}
          <span className={effective ? "text-emerald-400" : "text-zinc-500"}>
            {effective ? "Actif" : "Désactivé"}
          </span>
        </span>
        {!enabledGlobal && (
          <span className="text-[#EF9F27]">
            <Lock className="w-3 h-3 inline-block mr-1" />
            Désactivé au niveau plateforme (Ultra Admin)
          </span>
        )}
        <span data-testid="context-sync-today">
          Aujourd&apos;hui : <span className="text-zinc-300">{todayCount}</span> enregistrement(s)
        </span>
        {lastSyncedAt && (
          <span data-testid="context-sync-last">
            Dernière capture : <span className="text-zinc-300">{new Date(lastSyncedAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
          </span>
        )}
        <a
          href="/app/settings/context-sync"
          className="ml-auto text-[#378ADD] hover:underline"
          data-testid="context-sync-journal-link"
        >
          Voir le journal →
        </a>
      </div>
    </div>
  );
}

// Petit Toggle réutilisé pour la section sync (clone du toggle plus haut).
function Toggle({ active, disabled, onChange, testId }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange?.(!active)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
        active ? "bg-[#378ADD]" : "bg-zinc-700"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      data-testid={testId}
      aria-pressed={active}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          active ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}
